import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Award, Calendar, User, BookOpen, ArrowLeft, Search } from 'lucide-react';
import SEO from '@/components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyPage = () => {
  const { credentialId } = useParams();
  const [searchId, setSearchId] = useState(credentialId || '');
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  // Auto-verify if credential ID is in URL
  useEffect(() => {
    if (credentialId) {
      verifyCertificate(credentialId);
    }
  }, [credentialId]);

  const verifyCertificate = async (id) => {
    if (!id.trim()) return;
    
    setLoading(true);
    setError(null);
    setSearched(true);
    
    try {
      const response = await fetch(`${API_URL}/api/certificates/verify/${id.trim()}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Certificate not found. Please check the credential ID and try again.');
        } else {
          setError('An error occurred while verifying the certificate.');
        }
        setCertificate(null);
        return;
      }
      
      const data = await response.json();
      setCertificate(data);
    } catch (err) {
      setError('Unable to connect to verification service. Please try again later.');
      setCertificate(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    verifyCertificate(searchId);
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return 'N/A';
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFDF8' }}>
      <SEO 
        title="Verify Certificate — She Built This Youth Programme"
        description="Verify the authenticity of certificates issued by She Built This Youth Programme."
        keywords={['certificate verification', 'She Built This', 'youth programme', 'credential verification']}
        url="https://shebuiltthis.com/verify"
      />

      {/* Header */}
      <div className="border-b" style={{ borderColor: 'rgba(13, 13, 13, 0.1)' }}>
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link 
            to="/youth"
            className="inline-flex items-center gap-2 text-sm transition-colors hover:opacity-70"
            style={{ fontFamily: "'DM Mono', monospace", color: '#C8A448' }}
          >
            <ArrowLeft size={16} />
            Back to Youth Programme
          </Link>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-16">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: 'rgba(200, 164, 72, 0.1)' }}>
            <Award size={32} style={{ color: '#C8A448' }} />
          </div>
          <h1 
            className="text-3xl md:text-4xl mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#121212' }}
          >
            Certificate Verification
          </h1>
          <p 
            className="text-base"
            style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13, 13, 13, 0.6)' }}
          >
            Verify the authenticity of certificates issued by She Built This Youth Programme
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSubmit} className="mb-12">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value.toUpperCase())}
                placeholder="Enter Credential ID (e.g., SBT-2026-00001)"
                className="w-full h-12 px-4 pr-12 text-sm border transition-colors focus:outline-none"
                style={{ 
                  fontFamily: "'DM Mono', monospace",
                  borderColor: 'rgba(13, 13, 13, 0.2)',
                  backgroundColor: '#FFFFFF',
                }}
                data-testid="credential-input"
              />
              <Search 
                size={18} 
                className="absolute right-4 top-1/2 -translate-y-1/2"
                style={{ color: 'rgba(13, 13, 13, 0.3)' }}
              />
            </div>
            <button
              type="submit"
              disabled={loading || !searchId.trim()}
              className="h-12 px-6 text-sm tracking-wider uppercase transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ 
                fontFamily: "'DM Mono', monospace",
                backgroundColor: '#2E8B57',
                color: '#FFFFFF',
              }}
              data-testid="verify-btn"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </form>

        {/* Results */}
        {searched && (
          <div className="animate-fadeIn">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#C8A448', borderTopColor: 'transparent' }} />
                <p className="mt-4 text-sm" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13, 13, 13, 0.5)' }}>
                  Verifying certificate...
                </p>
              </div>
            ) : error ? (
              <div 
                className="p-8 text-center border"
                style={{ 
                  borderColor: 'rgba(220, 38, 38, 0.3)',
                  backgroundColor: 'rgba(220, 38, 38, 0.05)',
                }}
                data-testid="verification-error"
              >
                <XCircle size={48} className="mx-auto mb-4" style={{ color: '#DC2626' }} />
                <h3 
                  className="text-lg mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: '#DC2626' }}
                >
                  Verification Failed
                </h3>
                <p 
                  className="text-sm"
                  style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13, 13, 13, 0.6)' }}
                >
                  {error}
                </p>
              </div>
            ) : certificate ? (
              <div 
                className="border overflow-hidden"
                style={{ 
                  borderColor: 'rgba(46, 139, 87, 0.3)',
                  backgroundColor: '#FFFFFF',
                }}
                data-testid="verification-success"
              >
                {/* Success Header */}
                <div 
                  className="p-6 text-center"
                  style={{ backgroundColor: 'rgba(46, 139, 87, 0.05)' }}
                >
                  <CheckCircle size={48} className="mx-auto mb-4" style={{ color: '#2E8B57' }} />
                  <h3 
                    className="text-xl mb-1"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: '#2E8B57' }}
                  >
                    Verified Certificate
                  </h3>
                  <p 
                    className="text-sm"
                    style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13, 13, 13, 0.5)' }}
                  >
                    This certificate is authentic and valid
                  </p>
                </div>

                {/* Certificate Details */}
                <div className="p-6 space-y-4">
                  {/* Credential ID */}
                  <div className="flex items-start gap-3">
                    <Award size={18} className="mt-0.5 flex-shrink-0" style={{ color: '#C8A448' }} />
                    <div>
                      <p className="text-xs uppercase tracking-wider mb-1" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13, 13, 13, 0.4)' }}>
                        Credential ID
                      </p>
                      <p className="text-base font-semibold" style={{ fontFamily: "'DM Mono', monospace", color: '#121212' }}>
                        {certificate.credential_id}
                      </p>
                    </div>
                  </div>

                  {/* Recipient */}
                  <div className="flex items-start gap-3">
                    <User size={18} className="mt-0.5 flex-shrink-0" style={{ color: '#C8A448' }} />
                    <div>
                      <p className="text-xs uppercase tracking-wider mb-1" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13, 13, 13, 0.4)' }}>
                        Issued To
                      </p>
                      <p className="text-base" style={{ fontFamily: "'EB Garamond', serif", color: '#121212' }}>
                        {certificate.recipient_name}
                      </p>
                    </div>
                  </div>

                  {/* Course */}
                  <div className="flex items-start gap-3">
                    <BookOpen size={18} className="mt-0.5 flex-shrink-0" style={{ color: '#C8A448' }} />
                    <div>
                      <p className="text-xs uppercase tracking-wider mb-1" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13, 13, 13, 0.4)' }}>
                        Course Completed
                      </p>
                      <p className="text-base" style={{ fontFamily: "'EB Garamond', serif", color: '#121212' }}>
                        {certificate.course_title}
                      </p>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="flex items-start gap-3">
                    <Calendar size={18} className="mt-0.5 flex-shrink-0" style={{ color: '#C8A448' }} />
                    <div>
                      <p className="text-xs uppercase tracking-wider mb-1" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13, 13, 13, 0.4)' }}>
                        Issue Date
                      </p>
                      <p className="text-base" style={{ fontFamily: "'EB Garamond', serif", color: '#121212' }}>
                        {formatDate(certificate.issue_date)}
                      </p>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="pt-4 border-t" style={{ borderColor: 'rgba(13, 13, 13, 0.1)' }}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-wider" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13, 13, 13, 0.4)' }}>
                        Status
                      </span>
                      <span 
                        className="px-3 py-1 text-xs uppercase tracking-wider"
                        style={{ 
                          fontFamily: "'DM Mono', monospace",
                          backgroundColor: certificate.status === 'valid' ? 'rgba(46, 139, 87, 0.1)' : 'rgba(220, 38, 38, 0.1)',
                          color: certificate.status === 'valid' ? '#2E8B57' : '#DC2626',
                        }}
                      >
                        {certificate.status || 'Valid'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div 
                  className="px-6 py-4 text-center border-t"
                  style={{ 
                    borderColor: 'rgba(13, 13, 13, 0.1)',
                    backgroundColor: 'rgba(200, 164, 72, 0.03)',
                  }}
                >
                  <p 
                    className="text-xs"
                    style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13, 13, 13, 0.4)' }}
                  >
                    Issued by She Built This Youth Programme · shebuiltthis.co
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Info Section */}
        {!searched && (
          <div 
            className="p-6 text-center border"
            style={{ 
              borderColor: 'rgba(200, 164, 72, 0.2)',
              backgroundColor: 'rgba(200, 164, 72, 0.03)',
            }}
          >
            <p 
              className="text-sm mb-4"
              style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13, 13, 13, 0.6)' }}
            >
              Enter the Credential ID found on the certificate to verify its authenticity. 
              The Credential ID format is <strong>SBT-YYYY-XXXXX</strong>.
            </p>
            <p 
              className="text-xs"
              style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13, 13, 13, 0.4)' }}
            >
              Questions? Contact support@shebuiltthis.com
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default VerifyPage;
